import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ExternalLink,
  FileText,
  History,
  Monitor,
  Pencil,
  RefreshCw,
  Trash2,
  Workflow,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { ItemFlowRunsSheet } from "@/modules/flow/components/item-flow-runs-sheet";
import {
  getFolderNameById,
  getSourceName,
  mineApi,
  mineQueryKeys,
  openItemDetailInNewTab,
  useFolders,
  useItemActions,
  useRecordItemView,
  useSelectedItemId,
} from "@/modules/mine";
import { TextPreview } from "@/modules/mine/components/text-preview";
import { TextPreviewLoading } from "@/modules/mine/components/text-preview-loading";
import { useTextPreviewContent } from "@/modules/mine/components/use-text-preview-content";
import type { Item } from "@/modules/mine/types";
import { usePluginManager, ViewerRenderer } from "@/platform/plugins";
import { getErrorPresentation } from "@/shared/api/error-presentation";
import { useSheet } from "@/shared/components/sheet";
import type { UnifiedError } from "@/shared/types/api";
import { Button } from "@/shared/ui/button";

import { ImagePreview } from "./image-preview";
import {
  getImageSourceName,
  getPreviewTitle,
} from "./preview-utils";
import {
  type PreviewTab,
  WebsiteLivePreview,
  WebsiteSnapshotPreview,
} from "./website-preview";

function getPreviewErrorMessage(error: unknown) {
  if (typeof error === "object" && error !== null && "code" in error) {
    const presentation = getErrorPresentation(error as UnifiedError);
    return presentation.description || presentation.title;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "收藏详情暂时没有加载出来，请稍后重试。";
}

function PreviewState({
  description,
  icon,
  onClose,
  onRetry,
  title,
}: {
  description?: string;
  icon: ReactNode;
  onClose: () => void;
  onRetry?: () => void;
  title: string;
}) {
  return (
    <div className="flex h-full flex-col bg-background">
      <div className="flex h-14 shrink-0 items-center justify-end border-b border-border/40 px-4">
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:bg-muted"
          title="关闭预览"
          onClick={onClose}
        >
          <X size={18} />
        </Button>
      </div>
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="flex max-w-sm flex-col items-center text-center">
          <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            {icon}
          </div>
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          {description && (
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {description}
            </p>
          )}
          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              className="mt-5"
              onClick={onRetry}
            >
              <RefreshCw className="h-4 w-4" />
              重新加载
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewLoading({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex h-full flex-col bg-background">
      <div className="flex h-14 shrink-0 items-center justify-end border-b border-border/40 px-4">
        <Button
          className="size-8 text-muted-foreground hover:bg-muted"
          onClick={onClose}
          size="icon"
          title="关闭预览"
          variant="ghost"
        >
          <X size={18} />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <TextPreviewLoading />
      </div>
    </div>
  );
}

const MIN_SWITCH_LOADING_DURATION = 160;

const Preview = ({
  disableContentAnimation,
}: {
  disableContentAnimation?: boolean;
}) => {
  const { open: openSheet } = useSheet();
  const folders = useFolders();
  const { closePreview } = useItemActions();
  const recordItemView = useRecordItemView();
  const selectedItemId = useSelectedItemId();
  const selectedItemQuery = useQuery({
    queryKey: selectedItemId
      ? mineQueryKeys.itemDetail(selectedItemId)
      : ["mine", "items", "selected", null],
    queryFn: () => mineApi.getItem(selectedItemId ?? "", { silent: true }),
    enabled: !!selectedItemId,
  });
  const selectedItem = selectedItemQuery.data;
  const selectedTextContent = useTextPreviewContent(
    selectedItem?.format === "text" ? selectedItem : undefined,
  );
  const [iframeKey, setIframeKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [displayedItem, setDisplayedItem] = useState<Item | undefined>(
    selectedItem?.format === "text" ? undefined : selectedItem,
  );
  const [isTextPreviewRefreshing, setIsTextPreviewRefreshing] = useState(false);
  const [previewTab, setPreviewTab] = useState<PreviewTab>("live");
  const previousSelectedItemIdRef = useRef(selectedItemId);
  const recordedItemIdRef = useRef<string | null>(null);
  const selectionChangedAtRef = useRef(Date.now());

  const pluginManager = usePluginManager();
  const viewerMatch = useMemo(() => {
    if (!pluginManager || !displayedItem?.url) {
      return null;
    }

    const matches = pluginManager.urlMatcher.match(displayedItem.url);
    return matches.length > 0 ? matches[0] : null;
  }, [pluginManager, displayedItem?.url]);

  const handleViewFlowRuns = useCallback(() => {
    if (!displayedItem) {
      return;
    }

    void openSheet(ItemFlowRunsSheet, { item: displayedItem }, {
      className: "w-full sm:max-w-xl",
    });
  }, [displayedItem, openSheet]);

  const handleRefreshTextPreview = useCallback(async () => {
    setIsTextPreviewRefreshing(true);

    try {
      await Promise.all([
        selectedItemQuery.refetch(),
        selectedTextContent.refresh(),
      ]);
    } finally {
      setIsTextPreviewRefreshing(false);
    }
  }, [selectedItemQuery, selectedTextContent]);

  useEffect(() => {
    if (previousSelectedItemIdRef.current !== selectedItemId) {
      previousSelectedItemIdRef.current = selectedItemId;
      selectionChangedAtRef.current = Date.now();
    }

    if (!selectedItemId) {
      setDisplayedItem(undefined);
      return;
    }

    if (!selectedItem || selectedItem.id !== selectedItemId) {
      return;
    }

    if (selectedItem.format === "text" && selectedTextContent.isLoading) {
      return;
    }

    if (selectedItem.id !== displayedItem?.id) {
      const elapsed = Date.now() - selectionChangedAtRef.current;
      const remainingDuration = Math.max(
        0,
        MIN_SWITCH_LOADING_DURATION - elapsed,
      );

      const timer = setTimeout(() => {
        setDisplayedItem(selectedItem);
        setPreviewTab("live");
      }, remainingDuration);

      return () => clearTimeout(timer);
    }

    setDisplayedItem(selectedItem);
  }, [
    selectedItemId,
    selectedItem,
    selectedTextContent.isLoading,
    displayedItem?.id,
  ]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const nextWidth = entry.contentRect.width;
        setContainerWidth((currentWidth) =>
          currentWidth === nextWidth ? currentWidth : nextWidth,
        );
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!displayedItem || displayedItem.id !== selectedItemId
      || selectedItemQuery.isFetching || recordedItemIdRef.current === selectedItemId) {
      return;
    }

    recordedItemIdRef.current = selectedItemId;
    recordItemView(selectedItemId);
  }, [displayedItem, recordItemView, selectedItemId, selectedItemQuery.isFetching]);

  const shouldShowQueryState =
    !!selectedItemId && selectedItemId !== displayedItem?.id;

  if (selectedItemQuery.isError && shouldShowQueryState) {
    return (
      <PreviewState
        icon={<AlertCircle className="h-6 w-6 text-destructive" />}
        title="详情加载失败"
        description={getPreviewErrorMessage(selectedItemQuery.error)}
        onClose={closePreview}
        onRetry={() => void selectedItemQuery.refetch()}
      />
    );
  }

  if (shouldShowQueryState) {
    return <PreviewLoading onClose={closePreview} />;
  }

  if (!displayedItem) {
    return null;
  }

  const isImagePreview = displayedItem.format === "image";
  const showLabels = containerWidth > 680;
  const sourceName = isImagePreview
    ? getImageSourceName(displayedItem)
    : getSourceName(displayedItem.url || "");
  const title = getPreviewTitle(displayedItem, sourceName);
  const isTextPreview = displayedItem.format === "text";
  const folderName = getFolderNameById(displayedItem.folder_id ?? "", folders);
  const externalUrl = isImagePreview
    ? displayedItem?.url
    : displayedItem?.url ?? "";

  return (
    <div ref={containerRef} className="flex h-full flex-col bg-background">
      {!isImagePreview && (
        <div className="sticky top-0 z-10 border-b border-border/40 bg-background/50 backdrop-blur-sm">
          <div className="grid h-14 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center px-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="flex w-full min-w-0 max-w-[200px] items-center gap-2 text-sm font-bold text-foreground md:max-w-md">
                  {isTextPreview ? (
                    <>
                      <FileText size={15} className="shrink-0 text-muted-foreground" />
                      <span className="truncate">笔记详情</span>
                    </>
                  ) : (
                    <span className="truncate">{title}</span>
                  )}
                </span>
              </div>
            </div>

            <div className="shrink-0 justify-self-center">
              {displayedItem.format === "website" && (
                <div className="flex items-center rounded-lg border border-border/40 bg-muted/50 p-1">
                  <button
                    onClick={() => setPreviewTab("live")}
                    className={`flex items-center gap-2 rounded-md px-3 py-1 text-xs font-bold transition-all ${
                      previewTab === "live"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Monitor size={14} />
                    {showLabels && <span className="ml-1 mr-0.5">预览</span>}
                  </button>
                  <button
                    onClick={() => setPreviewTab("snapshot")}
                    className={`flex items-center gap-2 rounded-md px-3 py-1 text-xs font-bold transition-all ${
                      previewTab === "snapshot"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <History size={14} />
                    {showLabels && <span className="ml-1 mr-0.5">快照</span>}
                  </button>
                  <button
                    onClick={() => setPreviewTab("reader")}
                    className={`flex items-center gap-2 rounded-md px-3 py-1 text-xs font-bold transition-all ${
                      previewTab === "reader"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <FileText size={14} />
                    {showLabels && <span className="ml-1 mr-0.5">阅读</span>}
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 justify-self-end">
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-muted-foreground hover:text-foreground"
                onClick={handleViewFlowRuns}
                title="流程记录"
              >
                <Workflow size={16} />
              </Button>
              {isTextPreview && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground hover:text-foreground"
                    disabled={selectedItemQuery.isFetching || selectedTextContent.isRefreshing}
                    onClick={() => void handleRefreshTextPreview()}
                    title="刷新内容"
                    aria-label="刷新内容"
                  >
                    <RefreshCw
                      size={16}
                      className={selectedItemQuery.isFetching || selectedTextContent.isRefreshing
                        ? "animate-spin"
                        : undefined}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground hover:text-foreground"
                    onClick={() => openItemDetailInNewTab(displayedItem.id)}
                    title="在新标签页打开"
                    aria-label="在新标签页打开"
                  >
                    <ExternalLink size={16} />
                  </Button>
                </>
              )}
              {!isTextPreview && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground"
                    onClick={() => setIframeKey((prev) => prev + 1)}
                    title="刷新内容"
                  >
                    <RefreshCw size={16} />
                  </Button>
                  {(displayedItem.format === "website" || displayedItem.format === "image") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2 text-muted-foreground hover:text-primary"
                      title="在新窗口打开"
                      onClick={() => externalUrl && window.open(externalUrl, "_blank")}
                    >
                      <ExternalLink size={14} />
                    </Button>
                  )}
                  <div className="mx-1 h-4 w-px bg-border" />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground hover:text-foreground"
                    title="编辑"
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    title="删除"
                  >
                    <Trash2 size={16} />
                  </Button>
                </>
              )}
              <div className="mx-1 h-4 w-px bg-border" />
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-muted-foreground hover:bg-muted"
                title="关闭预览"
                onClick={() => closePreview()}
              >
                <X size={18} />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div
        key={displayedItem.id}
        className={`w-full flex-1 overflow-y-auto ${
          disableContentAnimation ? "" : "animate-content-slide-up"
        } ${isImagePreview ? "overflow-hidden" : ""}`}
      >
        {viewerMatch ? (
          <ViewerRenderer
            pluginId={viewerMatch.pluginId}
            item={{
              id: String(displayedItem.id),
              url: displayedItem?.url || "",
              title: displayedItem?.title || "",
              format: displayedItem.format,
            }}
            className="h-full w-full"
          />
        ) : (
          <>
            {displayedItem.format === "website" && (
              <>
                <WebsiteLivePreview
                  iframeKey={iframeKey}
                  item={displayedItem}
                  previewTab={previewTab}
                />
                <WebsiteSnapshotPreview
                  item={displayedItem}
                  previewTab={previewTab}
                  sourceName={sourceName}
                />
              </>
            )}
            {displayedItem.format === "text" && (
              isTextPreviewRefreshing ? (
                <TextPreviewLoading />
              ) : (
                <TextPreview
                  contentState={selectedTextContent}
                  folderName={folderName}
                  item={displayedItem}
                />
              )
            )}
            {isImagePreview && (
              <ImagePreview
                externalUrl={externalUrl}
                folderName={folderName}
                item={displayedItem}
                onClose={closePreview}
                onViewFlowRuns={handleViewFlowRuns}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Preview;
